import {
  CRITERES_ELIMINATOIRES,
  TYPES_CONTACT,
  evaluationSchema,
  majContactSchema,
  reponsesSchema,
  scoresSchema,
} from '@rh/shared';
import {
  FORMAT_SAISIES,
  type BaremeAffiche,
  type Brouillon,
  type EnvoiContact,
  type EtapeEnregistrement,
  type FinContact,
  type RefusEnvoi,
  type SaisieLocale,
} from './types';

/**
 * Lecture défensive des éléments relus d'IndexedDB. Une autre version de
 * l'application (onglet resté ouvert, mise à jour) a pu les écrire : ce qui
 * n'a pas la forme attendue est « illisible », jamais envoyé ni réécrit, et ne
 * quitte le téléphone qu'à son expiration (voir `expireLeBrut`).
 */

type Brut = Record<string, unknown>;

/**
 * Migrations de forme, par format de départ : `MIGRATIONS_FORMAT[n]` mène un
 * élément du format n au format n + 1. Aucune tant que le format 1 est le seul.
 */
export const MIGRATIONS_FORMAT: Record<number, (brut: Brut) => Brut> = {};

const objet = (valeur: unknown): valeur is Brut =>
  typeof valeur === 'object' && valeur !== null && !Array.isArray(valeur);
const texte = (valeur: unknown): valeur is string => typeof valeur === 'string' && valeur !== '';
const chaine = (valeur: unknown): valeur is string => typeof valeur === 'string';
const nombre = (valeur: unknown): valeur is number => typeof valeur === 'number' && Number.isFinite(valeur);
const entier = (valeur: unknown, min: number): valeur is number =>
  Number.isInteger(valeur) && (valeur as number) >= min;
const booleenOuNull = (valeur: unknown) => valeur === null || typeof valeur === 'boolean';
const instant = (valeur: unknown): valeur is string => texte(valeur) && !Number.isNaN(Date.parse(valeur));
const typeContact = (valeur: unknown) => (TYPES_CONTACT as readonly unknown[]).includes(valeur);
const CLES_MOTIF: readonly unknown[] = ['', ...CRITERES_ELIMINATOIRES.map((c) => c.cle)];

/** Ramène un élément au format courant ; null si son format est inconnu, plus récent ou sans migration. */
function migrer(brut: unknown, migrations: Record<number, (brut: Brut) => Brut>): Brut | null {
  if (!objet(brut)) return null;
  let courant = brut;
  // Au plus une étape par format connu : une migration qui ne fait pas avancer le format s'arrête là.
  for (let etapes = 0; etapes <= FORMAT_SAISIES; etapes += 1) {
    const format = courant.format;
    if (format === FORMAT_SAISIES) return courant;
    if (!entier(format, 0) || format > FORMAT_SAISIES) return null;
    const migration = migrations[format];
    if (!migration) return null;
    const suivant = migration({ ...courant });
    if (!objet(suivant) || !entier(suivant.format, format + 1)) return null;
    courant = suivant;
  }
  return null;
}

function baremeLisible(valeur: unknown): valeur is BaremeAffiche {
  return objet(valeur) && texte(valeur.id) && entier(valeur.version, 1);
}

function saisieLisible(valeur: unknown, type: unknown): valeur is SaisieLocale {
  if (!objet(valeur) || valeur.type !== type) return false;
  if (valeur.type === 'telephonique') {
    return (
      booleenOuNull(valeur.joignable) &&
      booleenOuNull(valeur.disponible) &&
      booleenOuNull(valeur.interesse) &&
      (valeur.potentiel === null || (entier(valeur.potentiel, 0) && valeur.potentiel <= 5)) &&
      chaine(valeur.rdv) &&
      chaine(valeur.lieu) &&
      chaine(valeur.rappel) &&
      chaine(valeur.observations)
    );
  }
  return (
    valeur.type === 'presentiel' &&
    reponsesSchema.safeParse(valeur.reponses).success &&
    chaine(valeur.observations) &&
    scoresSchema.safeParse(valeur.scores).success &&
    typeof valeur.eliminatoire === 'boolean' &&
    CLES_MOTIF.includes(valeur.motif) &&
    chaine(valeur.precision) &&
    typeof valeur.forcer === 'boolean' &&
    chaine(valeur.noteForcee) &&
    chaine(valeur.justification) &&
    (valeur.bareme === null || baremeLisible(valeur.bareme))
  );
}

function enregistrementLisible(valeur: unknown): valeur is EtapeEnregistrement {
  return objet(valeur) && entier(valeur.revision, 1) && majContactSchema.safeParse(valeur.corps).success;
}

function finLisible(valeur: unknown, type: unknown): valeur is FinContact {
  if (!objet(valeur) || !texte(valeur.envoiId)) return false;
  if (valeur.genre === 'cloture') return instant(valeur.termineLe);
  return (
    valeur.genre === 'evaluation' &&
    type === 'presentiel' &&
    instant(valeur.evalueeLe) &&
    objet(valeur.entree) &&
    evaluationSchema.safeParse(valeur.entree).success &&
    baremeLisible(valeur.bareme) &&
    nombre(valeur.noteAffichee) &&
    valeur.noteAffichee >= 0 &&
    valeur.noteAffichee <= 5
  );
}

function baremeRefuseLisible(valeur: unknown): boolean {
  return (
    valeur === null ||
    (objet(valeur) &&
      objet(valeur.affiche) &&
      texte(valeur.affiche.id) &&
      (valeur.affiche.version === null || entier(valeur.affiche.version, 1)) &&
      objet(valeur.applicable) &&
      texte(valeur.applicable.id) &&
      entier(valeur.applicable.version, 1))
  );
}

function refusLisible(valeur: unknown): valeur is RefusEnvoi {
  return (
    objet(valeur) &&
    (valeur.etape === 'enregistrement' || valeur.etape === 'fin') &&
    entier(valeur.statut, 0) &&
    (valeur.code === null || texte(valeur.code)) &&
    chaine(valeur.message) &&
    baremeRefuseLisible(valeur.bareme) &&
    nombre(valeur.le) &&
    typeof valeur.contenuEfface === 'boolean' &&
    (valeur.action === null || valeur.action === 'envoyer_evaluation_seule')
  );
}

function panneLisible(valeur: unknown): boolean {
  return valeur === null || (objet(valeur) && nombre(valeur.le) && chaine(valeur.message));
}

/** Champs communs aux envois et aux brouillons. */
function enteteLisible(brut: Brut): boolean {
  return (
    texte(brut.utilisateurId) &&
    texte(brut.contactId) &&
    texte(brut.candidatureId) &&
    typeContact(brut.type) &&
    instant(brut.debuteLe) &&
    nombre(brut.creeLe) &&
    nombre(brut.modifieLe) &&
    nombre(brut.expireLe)
  );
}

export function lireEnvoi(
  brut: unknown,
  migrations: Record<number, (brut: Brut) => Brut> = MIGRATIONS_FORMAT,
): EnvoiContact | null {
  const e = migrer(brut, migrations);
  if (!e || !enteteLisible(e)) return null;
  const lisible =
    entier(e.ordre, 1) &&
    entier(e.derniereRevision, 0) &&
    (e.enregistrement === null || enregistrementLisible(e.enregistrement)) &&
    (e.fin === null || finLisible(e.fin, e.type)) &&
    (e.saisie === null || saisieLisible(e.saisie, e.type)) &&
    // Un refus n'existe qu'à l'état refusé, et un élément refusé en porte toujours un.
    ((e.etat === 'en_attente' && e.refus === null) || (e.etat === 'refuse' && refusLisible(e.refus))) &&
    entier(e.tentatives, 0) &&
    nombre(e.prochaineTentativeLe) &&
    panneLisible(e.dernierePanne);
  return lisible ? (e as unknown as EnvoiContact) : null;
}

export function lireBrouillon(
  brut: unknown,
  migrations: Record<number, (brut: Brut) => Brut> = MIGRATIONS_FORMAT,
): Brouillon | null {
  const b = migrer(brut, migrations);
  if (!b || !enteteLisible(b)) return null;
  const lisible =
    saisieLisible(b.saisie, b.type) &&
    entier(b.revision, 0) &&
    (b.revisionMiseEnFile === null || entier(b.revisionMiseEnFile, 0)) &&
    chaine(b.onglet);
  return lisible ? (b as unknown as Brouillon) : null;
}

/**
 * Échéance d'un élément, même illisible : seul un `expireLe` numérique compte.
 * Sans échéance lisible, l'élément corrompu est effacé à la première purge.
 */
export function expireLeBrut(brut: unknown): number | null {
  return objet(brut) && nombre(brut.expireLe) ? brut.expireLe : null;
}
